/**
 * Wire schemas for module 6 (design management): drawings and their
 * revisions, transmittals, specification sections, design deliverables, RFIs
 * and technical queries, site visits and observations, practical completion
 * and the defects liability period.
 *
 * Synced to the device: drawings and drawing revisions (read-only — the
 * construction set travels to site; a superseded revision arrives marked),
 * specification sections (read-only), RFIs (raised and edited offline), site
 * visits and observations (recorded offline). The rest is online. Optional
 * fields default so a newer server never breaks an older client.
 */
import { z } from 'zod';

const id = z.string();
const nullableId = z.string().nullable().default(null);
const date = z.string().nullable().default(null);
const text = z.string().default('');
const version = z.number().int().nullable().default(null);
const count = z.coerce.number().catch(0).default(0);
const bool = z.boolean().catch(false).default(false);
const ids = z.array(z.string()).catch([]).default([]);

export const partySchema = z
  .object({ id: z.string(), name: z.string().default(''), role: z.string().default('') })
  .nullable()
  .catch(null)
  .default(null);
export type DesignParty = NonNullable<z.infer<typeof partySchema>>;

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

export const DISCIPLINES = [
  'architectural',
  'structural',
  'mep',
  'civil',
  'landscape',
  'other',
] as const;
export type Discipline = (typeof DISCIPLINES)[number];

export const SUITABILITIES = [
  'for_information',
  'for_comment',
  'for_approval',
  'for_construction',
  'as_built',
] as const;
export type Suitability = (typeof SUITABILITIES)[number];
/** What the contractor may issue its own (shop / as-built) drawings for. */
export const BUILDER_SUITABILITIES: readonly Suitability[] = [
  'for_information',
  'for_comment',
  'for_approval',
  'as_built',
];
/** A drawing in the construction set: its current revision is for construction or as built. */
export const SET_SUITABILITIES: readonly Suitability[] = ['for_construction', 'as_built'];

export const DRAWING_STATUSES = ['draft', 'current', 'superseded', 'void'] as const;
export type DrawingStatus = (typeof DRAWING_STATUSES)[number];
export const REVISION_STATES = ['current', 'superseded', 'previous'] as const;
export type RevisionState = (typeof REVISION_STATES)[number];
export const SHEET_SIZES = ['A0', 'A1', 'A2', 'A3', 'A4'] as const;

export const TRANSMITTAL_PURPOSES = [
  'for_information',
  'for_comment',
  'for_approval',
  'for_construction',
  'for_tender',
] as const;
export type TransmittalPurpose = (typeof TRANSMITTAL_PURPOSES)[number];

export const RFI_KINDS = ['rfi', 'technical_query', 'coordination'] as const;
export type RfiKind = (typeof RFI_KINDS)[number];
/** A coordination (clash) item is an RFI row: open → assigned → resolved → closed. */
export const RFI_STATUSES = ['open', 'answered', 'closed', 'void', 'assigned', 'resolved'] as const;
export type RfiStatus = (typeof RFI_STATUSES)[number];
export const RFI_PRIORITIES = ['normal', 'urgent'] as const;
/** Fields an RFI's raiser may edit while it is open and unanswered (offline: three-way merge). */
export const RFI_EDITABLE = [
  'subject',
  'question',
  'proposed_solution',
  'discipline',
  'priority',
  'drawing_ids',
  'zone_id',
  'task_id',
  'spec_section_id',
  'location',
  'required_by',
  'cost_impact',
  'time_impact',
  'assignee_party_id',
] as const;
/** Fields of a coordination item its raising side may edit while unresolved (offline: three-way merge). */
export const COORDINATION_EDITABLE = [
  'subject',
  'question',
  'proposed_solution',
  'disciplines',
  'priority',
  'drawing_ids',
  'drawing_refs',
  'zone_id',
  'spec_section_id',
  'location',
  'required_by',
  'cost_impact',
  'time_impact',
] as const;

export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Severity = (typeof SEVERITIES)[number];
export const VISIT_EDITABLE = [
  'visit_date',
  'purpose',
  'weather',
  'attendees',
  'summary',
  'arrived_at',
  'left_at',
] as const;
export const OBSERVATION_EDITABLE = [
  'description',
  'zone_id',
  'location',
  'severity',
  'action_required',
  'action_note',
] as const;
export const CONVERT_TARGETS = ['snag', 'ncr', 'instruction', 'close'] as const;
export type ConvertTarget = (typeof CONVERT_TARGETS)[number];

export const DESIGN_SUBMITTAL_KINDS = [
  'shop_drawing',
  'method_statement',
  'technical_approval',
  'om_manual',
  'as_built',
] as const;
export type DesignSubmittalKind = (typeof DESIGN_SUBMITTAL_KINDS)[number];

export const DELIVERABLE_STAGES = [
  'concept',
  'schematic',
  'developed',
  'technical',
  'construction',
  'as_built',
] as const;
export const DELIVERABLE_STATUSES = [
  'planned',
  'in_progress',
  'issued',
  'accepted',
  'cancelled',
] as const;
export type DeliverableStatus = (typeof DELIVERABLE_STATUSES)[number];

export const COMPLETION_STATUSES = [
  'requested',
  'inspection_scheduled',
  'certified',
  'refused',
  'withdrawn',
] as const;
export type CompletionStatus = (typeof COMPLETION_STATUSES)[number];

// ---------------------------------------------------------------------------
// Synced: drawings
// ---------------------------------------------------------------------------

export const drawingFileSchema = z
  .object({
    asset_id: z.string(),
    status: z.string().default('pending'),
    content_type: text,
    size_bytes: count,
    filename: text,
    has_derivatives: bool,
  })
  .nullable()
  .catch(null)
  .default(null);
export type DrawingFile = NonNullable<z.infer<typeof drawingFileSchema>>;

export const drawingRevisionSchema = z.object({
  id,
  project_id: id,
  drawing_id: id,
  code: z.string(),
  suitability: z.enum(SUITABILITIES).or(z.literal('')).catch(''),
  notes: text,
  issued_on: date,
  state: z.enum(REVISION_STATES).catch('previous'),
  superseded_at: date,
  superseded_by_id: nullableId,
  superseded_by_code: text,
  uploaded_by: text,
  created_at: date,
  file: drawingFileSchema,
  version,
});
export type DrawingRevision = z.infer<typeof drawingRevisionSchema>;

export const drawingSchema = z.object({
  id,
  project_id: id,
  number: z.string(),
  title: z.string(),
  description: text,
  discipline: z.enum(DISCIPLINES).catch('other'),
  sheet_size: text,
  scale: text,
  status: z.enum(DRAWING_STATUSES).catch('current'),
  status_reason: text,
  visibility: text,
  issued_to: ids,
  party: partySchema,
  current_revision_id: nullableId,
  current_revision: drawingRevisionSchema.nullable().catch(null).default(null),
  updated_at: date,
  can_issue: bool,
  version,
});
export type Drawing = z.infer<typeof drawingSchema>;

export const transmittalItemSchema = z.object({
  document_id: z.string(),
  revision_id: z.string(),
  reference: text,
  title: text,
  category: text,
  code: text,
  suitability: text,
});
export type TransmittalItem = z.infer<typeof transmittalItemSchema>;

export const transmittalSchema = z.object({
  id,
  project_id: id,
  reference: z.string(),
  number: count,
  from_party: partySchema,
  to_party: partySchema,
  purpose: z.enum(TRANSMITTAL_PURPOSES).catch('for_information'),
  subject: text,
  cover_note: text,
  items: z.array(transmittalItemSchema).catch([]).default([]),
  status: z.enum(['sent', 'acknowledged']).catch('sent'),
  sent_by_id: nullableId,
  sent_by: text,
  sent_at: date,
  acknowledged_by: text,
  acknowledged_at: date,
  acknowledgement_note: text,
  version,
});
export type Transmittal = z.infer<typeof transmittalSchema>;

export const drawingDetailSchema = drawingSchema.extend({
  revisions: z.array(drawingRevisionSchema).catch([]).default([]),
  transmittals: z.array(transmittalSchema).catch([]).default([]),
});
export type DrawingDetail = z.infer<typeof drawingDetailSchema>;

export const compareSchema = z.object({
  drawing: z.object({ id: z.string(), number: z.string(), title: z.string() }),
  older: drawingRevisionSchema,
  newer: drawingRevisionSchema,
  changes: z
    .array(
      z.object({
        field: z.string(),
        label: z.string(),
        older: z.unknown().optional(),
        newer: z.unknown().optional(),
      }),
    )
    .catch([])
    .default([]),
  revisions_between: count,
  same_file: bool,
});
export type RevisionCompare = z.infer<typeof compareSchema>;

// ---------------------------------------------------------------------------
// Synced: specification
// ---------------------------------------------------------------------------

export const specSectionSchema = z.object({
  id,
  project_id: id,
  number: z.string(),
  title: z.string(),
  discipline: z.enum(DISCIPLINES).catch('other'),
  document_id: nullableId,
  document_title: text,
  notes: text,
  party_id: text,
  version,
});
export type SpecSection = z.infer<typeof specSectionSchema>;

// ---------------------------------------------------------------------------
// Synced: RFIs
// ---------------------------------------------------------------------------

export const rfiAgeSchema = z
  .object({
    days_open: count,
    overdue: bool,
    days_overdue: count,
    response_days: z.number().nullable().catch(null).default(null),
    answered_late: bool,
  })
  .catch({
    days_open: 0,
    overdue: false,
    days_overdue: 0,
    response_days: null,
    answered_late: false,
  });
export type RfiAge = z.infer<typeof rfiAgeSchema>;

export const rfiHistorySchema = z.object({
  answer: text,
  by: text,
  party_id: text,
  at: date,
  follow_up: text,
});

export const rfiSchema = z.object({
  id,
  project_id: id,
  kind: z.enum(RFI_KINDS).catch('rfi'),
  reference: text,
  number: count,
  subject: z.string(),
  question: text,
  proposed_solution: text,
  discipline: text,
  priority: z.enum(RFI_PRIORITIES).catch('normal'),
  drawing_ids: ids,
  zone_id: nullableId,
  task_id: nullableId,
  spec_section_id: nullableId,
  location: text,
  required_by: date,
  cost_impact: bool,
  time_impact: bool,
  status: z.enum(RFI_STATUSES).catch('open'),
  raised_by_id: nullableId,
  raised_by: text,
  raised_by_party: partySchema,
  raised_at: date,
  assignee_party_id: nullableId,
  assignee_party: partySchema,
  answer: text,
  answered_by: text,
  answered_by_party: partySchema,
  answered_at: date,
  history: z.array(rfiHistorySchema).catch([]).default([]),
  closed_at: date,
  status_note: text,
  instruction_id: nullableId,
  instruction_reference: text,
  variation_id: nullableId,
  variation_reference: text,
  // Coordination items (kind "coordination"); empty on an RFI.
  disciplines: z.array(z.string()).catch([]).default([]),
  drawing_refs: text,
  assigned_discipline: text,
  assigned_at: date,
  age: rfiAgeSchema.optional(),
  created_at: date,
  can_answer: z.boolean().optional(),
  can_assign: z.boolean().optional(),
  can_resolve: z.boolean().optional(),
  version,
});
export type Rfi = z.infer<typeof rfiSchema>;

export const rfiAgeingSchema = z.object({
  as_of: z.string(),
  open: count,
  overdue: count,
  answered: count,
  average_response_days: z.number().nullable().catch(null).default(null),
  answered_late: count,
  buckets: z
    .array(
      z.object({ label: z.string(), from: count, to: z.number().nullable().default(null), count }),
    )
    .catch([])
    .default([]),
  by_assignee: z
    .array(z.object({ party: partySchema, open: count, overdue: count, oldest_days: count }))
    .catch([])
    .default([]),
  by_discipline: z
    .array(z.object({ discipline: text, open: count, overdue: count }))
    .catch([])
    .default([]),
  oldest: z.array(rfiSchema).catch([]).default([]),
});
export type RfiAgeing = z.infer<typeof rfiAgeingSchema>;

// ---------------------------------------------------------------------------
// Synced: site visits and observations
// ---------------------------------------------------------------------------

export const siteVisitSchema = z.object({
  id,
  project_id: id,
  reference: text,
  number: count,
  visit_date: z.string(),
  purpose: z.string(),
  weather: text,
  attendees: z.array(z.string()).catch([]).default([]),
  summary: text,
  arrived_at: z.string().nullable().catch(null).default(null),
  left_at: z.string().nullable().catch(null).default(null),
  status: z.enum(['draft', 'issued']).catch('draft'),
  recorded_by_id: nullableId,
  recorded_by: text,
  party: partySchema,
  issued_at: date,
  created_at: date,
  version,
});
export type SiteVisit = z.infer<typeof siteVisitSchema>;

export const observationSchema = z.object({
  id,
  project_id: id,
  visit_id: z.string(),
  reference: text,
  number: count,
  description: z.string(),
  zone_id: nullableId,
  location: text,
  severity: z.enum(SEVERITIES).catch('low'),
  action_required: bool,
  action_note: text,
  status: z.enum(['open', 'actioned', 'closed']).catch('open'),
  recorded_by: text,
  converted_type: text,
  converted_id: nullableId,
  converted_reference: text,
  converted_at: date,
  created_at: date,
  version,
});
export type Observation = z.infer<typeof observationSchema>;

export const visitReportSchema = siteVisitSchema.extend({
  observations: z
    .array(
      observationSchema.extend({
        photos: z
          .array(z.object({ id: z.string(), status: text, caption: text }))
          .catch([])
          .default([]),
      }),
    )
    .catch([])
    .default([]),
});
export type VisitReport = z.infer<typeof visitReportSchema>;

// ---------------------------------------------------------------------------
// Online: deliverables, completion, the overview
// ---------------------------------------------------------------------------

export const deliverableSchema = z.object({
  id,
  project_id: id,
  reference: text,
  title: z.string(),
  discipline: z.enum(DISCIPLINES).catch('other'),
  stage: z.enum(DELIVERABLE_STAGES).catch('construction'),
  due_date: date,
  status: z.enum(DELIVERABLE_STATUSES).catch('planned'),
  progress_percent: count,
  lead: text,
  required_for: text,
  document_ids: ids,
  notes: text,
  issued_at: date,
  party: partySchema,
  overdue: bool,
  days_to_due: z.number().nullable().catch(null).default(null),
  version,
});
export type Deliverable = z.infer<typeof deliverableSchema>;

export const completionSchema = z.object({
  id,
  project_id: id,
  reference: text,
  status: z.enum(COMPLETION_STATUSES).catch('requested'),
  scope: text,
  requested_by: text,
  requested_by_id: nullableId,
  requested_by_party: partySchema,
  requested_at: date,
  proposed_date: date,
  request_note: text,
  inspection_at: date,
  inspection_note: text,
  decided_by: text,
  decided_by_party: partySchema,
  decided_at: date,
  decision_note: text,
  completion_date: date,
  dlp_months: z.number().nullable().catch(null).default(null),
  dlp_end: date,
  dlp_days_left: z.number().nullable().catch(null).default(null),
  in_dlp: bool,
  snag_ids: ids,
  version,
});
export type Completion = z.infer<typeof completionSchema>;

export const dlpDefectSchema = z.object({
  id: z.string(),
  reference: text,
  title: z.string(),
  location: text,
  severity: z.enum(SEVERITIES).catch('medium'),
  status: text,
  due_date: date,
  raised_at: date,
  raised_by_party: partySchema,
  assignee_party: partySchema,
  overdue: bool,
});
export type DlpDefect = z.infer<typeof dlpDefectSchema>;

export const completionOverviewSchema = z.object({
  current: completionSchema.nullable().catch(null).default(null),
  history: z.array(completionSchema).catch([]).default([]),
  can_request: bool,
  can_certify: bool,
  can_report_defects: bool,
  dlp_months: z.number().catch(12).default(12),
  snags: z
    .object({ open: count, total: count, attached: count })
    .nullable()
    .catch(null)
    .default(null),
  defects: z.array(dlpDefectSchema).catch([]).default([]),
});
export type CompletionOverview = z.infer<typeof completionOverviewSchema>;

export const designSummarySchema = z.object({
  drawings: z.object({ current: count, construction_set: count, drafts: count }),
  rfis: z.object({ open: count, overdue: count, awaiting_me: count }),
  coordination: z
    .object({ open: count, overdue: count, assigned_to_me: count })
    .catch({ open: 0, overdue: 0, assigned_to_me: 0 })
    .default({ open: 0, overdue: 0, assigned_to_me: 0 }),
  submittals: z.object({ under_review: count, with_contractor: count, returned: count }),
  transmittals: z.object({ to_acknowledge: count }),
  deliverables: z.object({ open: count, overdue: count }),
  site_visits: z.object({ drafts: count, this_month: count }),
});
export type DesignSummary = z.infer<typeof designSummarySchema>;
